import React, { useContext } from 'react';
import CompanySectionLayout from '../../../../component/B2B/CompanySectionLayout';
import { SidebarContext } from '../../../../context/SidebarContext';
import { getLocalStorageItem } from '../../../../utils/helper';

/**
 * Company panel → Self Development: the umbrella over the six tools the app
 * groups under that name.
 *
 *   Overview      how many people practise anything at all
 *   Gratitude     things somebody is grateful for
 *   Cleanse       a CBT-style thought record: thought, evidence, reframe
 *   Journal       free journalling ("Notes" in the app)
 *   Goals         a goal with a checklist and a due date
 *   Vision Board  a board of things somebody wants
 *   Affirmations  affirmations people write for themselves, and Shoorah's own
 *
 * Vision Board used to be a section of its own. It sits here now because it is
 * the same habit as the other five and reads as noise on its own — its pages
 * are unchanged, just re-parented, and the old /vision-board URLs redirect in.
 *
 * WHAT THIS SECTION WILL NEVER SHOW: what anybody wrote. Every one of these six
 * collections is somebody's private writing, and none of it — not a gratitude,
 * a journal page, a thought, a goal, an affirmation or a board — is read by any
 * endpoint behind these pages. Take-up, follow-through and whether the habit
 * stuck is the whole remit.
 */
const CompanySelfDevelopmentLayout = ({ children }) => {
  const { isB2BFansMode } = useContext(SidebarContext);
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const population = isB2BFansMode ? 'members' : 'employees';
  const base = `/${slug}/self-development`;

  const pages = [
    {
      label: 'Overview',
      path: base,
      description:
        'Take-up across all six tools, how they compare, and who keeps more than one going'
    },
    {
      label: 'Gratitude',
      path: `${base}/gratitude`,
      description: 'How many people keep a gratitude practice, and how often'
    },
    {
      label: 'Cleanse',
      path: `${base}/cleanse`,
      description: 'Thought records started, and how many make it all the way to the reframe'
    },
    {
      label: 'Journal',
      path: `${base}/journal`,
      description: 'Journalling take-up, and what people do with an entry once it is written'
    },
    {
      label: 'Goals',
      path: `${base}/goals`,
      description: 'Goals set, goals achieved, and the ones that have run past their date'
    },
    {
      label: 'Vision Board',
      path: `${base}/vision-board`,
      description: 'Boards created, and how many get filled in rather than abandoned'
    },
    {
      label: 'Affirmations',
      path: `${base}/affirmations`,
      description: 'Affirmations people write for themselves, and the ones they play from Shoorah'
    },
    {
      label: isB2BFansMode ? 'Members' : 'Employees',
      path: `${base}/users`,
      description: `Per-${population.replace(/s$/, '')} practice across all six tools`
    }
  ];

  return (
    <CompanySectionLayout
      title="Self Development"
      subtitle={`How many of your ${population} work on themselves in the app, and which of the six tools they stick with.`}
      pages={pages}
    >
      {children}
    </CompanySectionLayout>
  );
};

export default CompanySelfDevelopmentLayout;
